/**
 * Speech INPUT — the native Web Speech API: zero backend, zero credentials,
 * client-only. Cloned from the console (tc-lab src/useSpeech.ts), input half
 * only: read-aloud TTS (the console's ElevenLabs /voice proxy) is deferred —
 * when LV grows it, it lands as its own server route next to the chat's.
 *
 * No official TS types ship for the speech APIs, hence the narrow `any`
 * usage confined to this one file.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

export function useSpeechInput(onResult: (text: string) => void) {
  const [listening, setListening] = useState(false)
  const [supported, setSupported] = useState(true)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recRef = useRef<any>(null)
  const onResultRef = useRef(onResult)
  onResultRef.current = onResult

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any
    const SR = w.SpeechRecognition || w.webkitSpeechRecognition
    if (!SR) { setSupported(false); return }
    const rec = new SR()
    rec.continuous = false
    rec.interimResults = false
    rec.lang = 'en-US'
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rec.onresult = (e: any) => {
      const text = Array.from(e.results as ArrayLike<{ 0: { transcript: string } }>)
        .map((r) => r[0].transcript).join(' ')
      onResultRef.current(text)
    }
    rec.onend = () => setListening(false)
    rec.onerror = () => setListening(false)
    recRef.current = rec
    return () => { rec.onresult = null; rec.onend = null; rec.onerror = null }
  }, [])

  const toggle = useCallback(() => {
    if (!recRef.current) return
    if (listening) {
      recRef.current.stop()
      setListening(false)
    } else {
      try { recRef.current.start(); setListening(true) } catch { /* already running */ }
    }
  }, [listening])

  return { listening, supported, toggle }
}
